import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent, ReactNode } from "react";

interface Props {
    rowClassName: string;
    children: ReactNode;
}

export function PmScroller({ rowClassName, children }: Props) {
    const row = useRef<HTMLDivElement>(null);
    const drag = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false });
    const [edges, setEdges] = useState({ start: false, end: false });

    const measure = useCallback(() => {
        const node = row.current;
        if (!node) {
            setEdges({ start: false, end: false });
            return;
        }
        const max = node.scrollWidth - node.clientWidth;
        const pos = Math.abs(node.scrollLeft);
        setEdges({ start: pos > 4, end: pos < max - 4 });
    }, []);

    useEffect(() => {
        const node = row.current;
        if (!node) {
            return;
        }
        measure();
        const suppress = (event: MouseEvent) => {
            if (drag.current.moved) {
                event.preventDefault();
                event.stopPropagation();
                drag.current.moved = false;
            }
        };
        node.addEventListener("click", suppress, true);
        window.addEventListener("resize", measure);
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => {
            node.removeEventListener("click", suppress, true);
            window.removeEventListener("resize", measure);
            observer.disconnect();
        };
    }, [measure]);

    useEffect(() => {
        measure();
    }, [children, measure]);

    function scrollRow(direction: number) {
        const node = row.current;
        if (!node) {
            return;
        }
        const rtl = window.getComputedStyle(node).direction === "rtl";
        node.scrollBy({ left: direction * node.clientWidth * 0.8 * (rtl ? -1 : 1), behavior: "smooth" });
    }

    function onDragStart(event: PointerEvent<HTMLDivElement>) {
        const node = event.currentTarget;
        if (event.button !== 0) {
            return;
        }
        drag.current = { active: true, startX: event.clientX, scrollLeft: node.scrollLeft, moved: false };
        node.classList.add("pm-dragging");
    }

    function onDragMove(event: PointerEvent<HTMLDivElement>) {
        if (!drag.current.active) {
            return;
        }
        const dx = event.clientX - drag.current.startX;
        if (Math.abs(dx) > 4) {
            drag.current.moved = true;
        }
        event.currentTarget.scrollLeft = drag.current.scrollLeft - dx;
    }

    function onDragEnd(event: PointerEvent<HTMLDivElement>) {
        if (!drag.current.active) {
            return;
        }
        drag.current.active = false;
        event.currentTarget.classList.remove("pm-dragging");
    }

    return (
        <div className="pm-home-viewport">
            <button type="button" tabIndex={-1} aria-hidden="true"
                    className={`pm-home-edge pm-home-edge-start ${edges.start ? "is-visible" : ""}`}
                    onClick={() => scrollRow(-1)}>
                <span className="pm-home-edge-icon"><i className="fa fa-chevron-left" /></span>
            </button>
            <button type="button" tabIndex={-1} aria-hidden="true"
                    className={`pm-home-edge pm-home-edge-end ${edges.end ? "is-visible" : ""}`}
                    onClick={() => scrollRow(1)}>
                <span className="pm-home-edge-icon"><i className="fa fa-chevron-right" /></span>
            </button>
            <div className={rowClassName} ref={row} onScroll={measure}
                 onPointerDown={onDragStart} onPointerMove={onDragMove}
                 onPointerUp={onDragEnd} onPointerLeave={onDragEnd}>
                {children}
            </div>
        </div>
    );
}
